import { useState } from 'react'
import { MenuItem, Stack, TextField, Typography } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import useAuditLog from '../../io/useAuditLog'
import { AuditAction, AuditCategory } from '../../io/api/audit/data/AuditEntry'
import AuditLogTable from './AuditLogTable'

const CATEGORIES: ('' | AuditCategory)[] = ['', 'Entity', 'EntityType', 'Permission', 'SemanticLink']

const ACTIONS_BY_CATEGORY: Record<AuditCategory, AuditAction[]> = {
  Entity: [
    'EntityCreate',
    'EntityValueUpdate',
    'EntityLinkUpdate',
    'EntityDelete',
    'EntityRecover',
    'EntityPublish',
    'EntityInviteUser',
  ],
  EntityType: [
    'EntityTypeCreate',
    'EntityTypeNameUpdate',
    'EntityTypeFieldUpdate',
    'EntityTypeLabelUpdate',
    'EntityTypeKindUpdate',
    'EntityTypeDelete',
    'EntityTypeRecover',
  ],
  Permission: ['PermissionGrant', 'PermissionUpdate', 'PermissionRevoke'],
  SemanticLink: ['SemanticLinkCreate', 'SemanticLinkDelete'],
}

const AuditPage = () => {
  const [category, setCategory] = useState<'' | AuditCategory>('')
  const [action, setAction] = useState<'' | AuditAction>('')
  const [actor, setActor] = useState('')
  const [targetId, setTargetId] = useState('')
  const [from, setFrom] = useState<Dayjs | null>(null)
  const [to, setTo] = useState<Dayjs | null>(null)

  const { entries, loadMore, hasMore, loading, error } = useAuditLog({
    category: category || undefined,
    action: action || undefined,
    actor: actor.trim() || undefined,
    targetId: targetId.trim() || undefined,
    from: from?.isValid() ? from.toISOString() : undefined,
    to: to?.isValid() ? to.toISOString() : undefined,
  })

  const actionOptions: ('' | AuditAction)[] = category ? ['', ...ACTIONS_BY_CATEGORY[category]] : ['']

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack padding={3} spacing={3}>
        <Typography variant="h4">Audit log</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <DateTimePicker
            label="From"
            value={from}
            onChange={setFrom}
            maxDateTime={to ?? undefined}
            ampm={false}
            format="YYYY-MM-DD HH:mm"
            slotProps={{
              textField: { size: 'small', sx: { minWidth: 220 } },
              field: { clearable: true },
            }}
          />
          <DateTimePicker
            label="To"
            value={to}
            onChange={setTo}
            minDateTime={from ?? undefined}
            maxDateTime={dayjs()}
            ampm={false}
            format="YYYY-MM-DD HH:mm"
            slotProps={{
              textField: { size: 'small', sx: { minWidth: 220 } },
              field: { clearable: true },
            }}
          />
          <TextField
            size="small"
            label="Target id"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            sx={{ minWidth: 320 }}
          />
          <TextField
            size="small"
            label="Actor (user entity id)"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            sx={{ minWidth: 320 }}
          />
          <TextField
            size="small"
            select
            label="Category"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value as '' | AuditCategory)
              setAction('')
            }}
            sx={{ minWidth: 180 }}
          >
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {c || 'All'}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Action"
            value={action}
            onChange={(e) => setAction(e.target.value as '' | AuditAction)}
            sx={{ minWidth: 220 }}
            disabled={!category}
          >
            {actionOptions.map((a) => (
              <MenuItem key={a} value={a}>
                {a || 'All'}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <AuditLogTable entries={entries} loading={loading} error={error} hasMore={hasMore} loadMore={loadMore} />
      </Stack>
    </LocalizationProvider>
  )
}

export default AuditPage
